import { useEntitlements } from '@/composables/useEntitlements';
import i18n from '@/i18n';
import { evaluatePlanGate } from '@/router/planGate';
import { routes } from '@/router/routes';
import { useAuthStore } from '@/stores/auth';
import { usePlanGateStore } from '@/stores/planGate';
import { useProgressStore } from '@/stores/progress';
import { useToastStore } from '@/stores/toast';
import { createRouter, createWebHistory } from 'vue-router';

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior(to, from, savedPosition) {
        // Don't scroll when only query params or hash change (e.g. tab switching, filters)
        if (to.path === from.path) {
            return savedPosition ?? false;
        }
        return savedPosition || { left: 0, top: 0 };
    },
    routes,
});

router.beforeEach(async (to, from) => {
    // Start progress indicator on navigation
    const progress = useProgressStore();
    progress.start();

    const auth = useAuthStore();
    const toast = useToastStore();

    // SECURITY: hydrate the auth state from the API on first navigation.
    // The `user` object is intentionally NOT persisted to localStorage (see
    // `useAuthStore`'s persist config), so on every app boot we re-fetch it
    // from `/api/user`. This guarantees that role / email_verified /
    // profile_complete cannot be forged client-side via tampered localStorage.
    if (!auth.ready) {
        await auth.init();
    }

    const isProtected = !to.meta?.public;
    const isAdminRoute = to.meta?.admin;

    // Check if user is authenticated for protected routes
    if (isProtected && !auth.isAuthenticated) {
        return { name: 'login', query: { redirect: to.fullPath } };
    }

    // Check if user is admin for admin routes
    if (isAdminRoute && !auth.isAdmin()) {
        // Show error message and redirect non-admin users to home page
        toast.error(i18n.global.t('error.adminOnly'));
        return { name: 'home' };
    }

    // Plan-gating (MILO-282). Frontend upsells, backend enforces.
    if (isProtected && auth.isAuthenticated) {
        const entitlements = useEntitlements();
        const planGate = usePlanGateStore();
        const result = evaluatePlanGate(to, from, {
            allows: entitlements.allows,
            isReadOnly: entitlements.isReadOnly.value,
            trigger: (error) => planGate.trigger(error),
        });
        if (result !== undefined) return result;
    }

    // Update page title
    const titleKey = (to.meta?.title || 'pages.home') as string;
    const title = i18n.global.t(titleKey);
    const appName = import.meta.env.VITE_APP_NAME || 'App';
    const newTitle = `${title} | ${appName}`;
    if (document.title !== newTitle) {
        document.title = newTitle;
    }

    // Update meta description (and clear a stale one when the route has none — the
    // public token pages intentionally carry no SEO description).
    const metaDescription = document.querySelector('meta[name="description"]');
    if (to.meta?.description) {
        const description = i18n.global.t(to.meta.description as string);
        if (metaDescription) {
            metaDescription.setAttribute('content', description);
        } else {
            const el = document.createElement('meta');
            el.setAttribute('name', 'description');
            el.setAttribute('content', description);
            document.head.appendChild(el);
        }
    } else if (metaDescription) {
        metaDescription.remove();
    }

    // Keep public (token) pages out of search indexes. The access token is the URL
    // path segment, so these must never be indexed even if a link leaks — and
    // robots.txt alone does not stop indexing of an already-discovered URL.
    const robotsMeta = document.querySelector('meta[name="robots"]');
    if (to.meta?.public) {
        if (robotsMeta) {
            robotsMeta.setAttribute('content', 'noindex, nofollow');
        } else {
            const el = document.createElement('meta');
            el.setAttribute('name', 'robots');
            el.setAttribute('content', 'noindex, nofollow');
            document.head.appendChild(el);
        }
    } else if (robotsMeta) {
        robotsMeta.remove();
    }
});

// Finish progress indicator after navigation
router.afterEach(() => {
    const progress = useProgressStore();
    progress.finish();
});

// Also finish progress on navigation errors/cancellations
router.onError(() => {
    const progress = useProgressStore();
    progress.finish();
});

export default router;
